// The Shot Details side panel — ported from renderers-shotmap.js shotDetailsPanelHtml +
// renderShotPlacementChart + renderXgChart. All values come from shotmap-view.ts (pure);
// this paints them with the legacy's exact markup.

import { Fragment } from "react";
import type { ProtoEvent } from "@catbento/shared";
import type { EventContext } from "../engines/event-map-kit.js";
import { obvChartRows } from "./event-details-view.js";
import { DetailGroup, DivergingBarChart } from "./EventDetailParts.js";
import {
  GOAL_CHART_VB_H,
  GOAL_CHART_VB_W,
  GOALKEEPER_SVG_PATHS,
  shotDetails,
  shotPlacement,
  xgChartRows,
} from "./shotmap-view.js";

export function ShotDetailsPanel({ ctx, shot }: { ctx: EventContext; shot: ProtoEvent }) {
  const d = shotDetails(ctx, shot);
  return (
    <div className="event-detail-grid">
      <DetailGroup label="Opposition" span2>
        <div className="event-detail-value">{d.opposition}</div>
      </DetailGroup>
      <DetailGroup label="Date">
        <div className="event-detail-value">{d.date}</div>
      </DetailGroup>
      <DetailGroup label="Result">
        <div className={`event-detail-value ${d.resultClass}`}>{d.resultText}</div>
      </DetailGroup>
      <DetailGroup label="Time">
        <div className="event-detail-value">{d.time}</div>
      </DetailGroup>
      <DetailGroup label="Outcome">
        <span className={`event-detail-pill ${d.outcomePillClass}`}>{d.outcome}</span>
      </DetailGroup>
      <ShotPlacementGroup shot={shot} />
      <DetailGroup label="Expected Goals (xG)" span2>
        <DivergingBarChart rows={xgChartRows(shot)} />
      </DetailGroup>
      <DetailGroup label="On-Ball Value (OBV)" span2>
        <DivergingBarChart rows={obvChartRows(shot)} />
      </DetailGroup>
      <DetailGroup label="Body Part">
        <span className="event-detail-pill">{d.bodyPart}</span>
      </DetailGroup>
      <DetailGroup label="Type">
        <span className="event-detail-pill">{d.type}</span>
      </DetailGroup>
      <DetailGroup label="Technique">
        <span className="event-detail-pill">{d.technique}</span>
      </DetailGroup>
      <DetailGroup label="Position">
        <span className="event-detail-pill">{d.position}</span>
      </DetailGroup>
      <DetailGroup label="Play Pattern">
        <span className="event-detail-pill">{d.playPattern}</span>
      </DetailGroup>
      <DetailGroup label="Characteristics">
        <div className="event-detail-value event-detail-tags">
          {d.tags.length
            ? d.tags.map((t, i) => (
                <Fragment key={t}>
                  {i > 0 && " "}
                  <span className="event-detail-pill">{t}</span>
                </Fragment>
              ))
            : "—"}
        </div>
      </DetailGroup>
    </div>
  );
}

/** The goal-mouth chart (omitted with no end_location). A skied shot's dot deliberately
 *  paints above the chart, over its own label (overflow:visible in styles.css). */
function ShotPlacementGroup({ shot }: { shot: ProtoEvent }) {
  const p = shotPlacement(shot);
  if (!p) return null;
  return (
    <DetailGroup label="Shot Placement" span2>
      <svg className="shot-placement-svg" viewBox={`0 0 ${GOAL_CHART_VB_W} ${GOAL_CHART_VB_H}`} preserveAspectRatio="xMidYMid meet">
        <rect className="shot-placement-frame-fill" x={p.postL} y={p.bar} width={p.postR - p.postL} height={GOAL_CHART_VB_H - p.bar}></rect>
        {p.keeper && (
          <svg
            className="shot-placement-keeper"
            x={p.keeper.x}
            y={p.keeper.y}
            width={p.keeper.width}
            height={p.keeper.height}
            viewBox="0 0 564.7 1082.2"
            preserveAspectRatio="xMidYMax meet"
          >
            {GOALKEEPER_SVG_PATHS.map((d, i) => (
              <path key={i} d={d} />
            ))}
          </svg>
        )}
        <line className="shot-placement-frame-line" x1={p.postL} y1={p.bar} x2={p.postL} y2={GOAL_CHART_VB_H}></line>
        <line className="shot-placement-frame-line" x1={p.postR} y1={p.bar} x2={p.postR} y2={GOAL_CHART_VB_H}></line>
        <line className="shot-placement-frame-line" x1={p.postL} y1={p.bar} x2={p.postR} y2={p.bar}></line>
        <line className="shot-placement-ground" x1={0} y1={GOAL_CHART_VB_H} x2={GOAL_CHART_VB_W} y2={GOAL_CHART_VB_H}></line>
        <circle className="shot-placement-dot" cx={p.dot.cx} cy={p.dot.cy} r={3} fill={p.dot.fill}></circle>
      </svg>
    </DetailGroup>
  );
}
